import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRef } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { 
    Collapse, 
    List, 
    ListItemButton, 
    ListItemIcon, 
    ListItemText, 
    Tooltip, 
    Typography, 
    Popper, 
    Paper, 
    ClickAwayListener 
} from '@mui/material';

// project imports
import NavItem from '../NavItem';
import { MENU_OPEN } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

const NavCollapse = ({ menu, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const anchorRef = useRef(null);
    const wasDrawerOpen = useRef(leftDrawerOpened);
    const [open, setOpen] = useState(false);
    
    // Check if any child item is active based on URL
    const isChildActive = () => {
        const checkActiveChild = (items) => {
            for (let i = 0; i < items.length; i++) {
                if (items[i].type === 'item' && items[i].url) {
                    // For the root path, special handling
                    if (items[i].url === '/' || items[i].url === '') {
                        if (document.location.pathname === '/' || document.location.pathname === '') {
                            return true;
                        }
                        continue;
                    }
                    
                    // For other paths, ensure exact or nested path match
                    const currentPath = document.location.pathname;
                    if (currentPath.startsWith(items[i].url)) {
                        if (currentPath === items[i].url || 
                            currentPath.startsWith(items[i].url + '/') || 
                            items[i].url === currentPath.substring(0, items[i].url.length)) {
                            return true;
                        }
                    }
                }
                if (items[i].type === 'collapse' && items[i].children) {
                    if (checkActiveChild(items[i].children)) {
                        return true;
                    }
                }
            }
            return false;
        };
        
        return menu.children && checkActiveChild(menu.children);
    };
    
    // Check if this collapse menu should be active
    const active = isChildActive();
    
    // Open the collapse menu if a child is active on initial load
    useEffect(() => {
        if (active) {
            setOpen(true);
            dispatch({ type: MENU_OPEN, id: menu.id });
        }
        // Close the popper when drawer is closed
    if (!leftDrawerOpened) {
        setOpen(false);
    }
    }, [active, dispatch, menu.id,]);
  useEffect(() => {
    const wasOpen = wasDrawerOpen.current;
    const isNowClosed = !leftDrawerOpened && wasOpen;

    if (isNowClosed && open) {
        // Defer the close slightly to avoid flicker
        const timeout = setTimeout(() => {
            setOpen(false);
        }, 0);
        return () => clearTimeout(timeout);
    }

    wasDrawerOpen.current = leftDrawerOpened;
}, [leftDrawerOpened, open]);
    
    const handleClick = () => {
        setOpen((prevOpen) => !prevOpen);
        
        // Only select/highlight this menu when clicked, not the children
        dispatch({ type: MENU_OPEN, id: menu.id });
    };

    // menu collapse & item
    const menus = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} setOpen={setOpen} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = menu.icon ? (
        <Icon 
            strokeWidth={1.5} 
            size="24px" 
            style={{ 
                marginTop: 'auto', 
                marginBottom: 'auto', 
                color: active ? "#fff" : "rgb(230, 234, 237)" 
            }} 
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: active ? 11 : 6,
                height: active ? 10 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );
    
    return (
        <>
            <Tooltip 
                sx={{
                    backgroundColor: '#292A2E',
                    color: '#fff',
                    fontSize: '14px',
                    px: 2,
                    py: 1,
                    borderRadius: '12px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        right: 12,
                        width: '40px',
                        height: '4px',
                        backgroundColor: '#292A2E',
                        borderRadius: '2px'
                    }
                }}  
                title={menu.tooltip || ''} 
                placement="right" 
                disableHoverListener={!menu.tooltip} 
                arrow
            >
                <ListItemButton
                    sx={{
                        borderRadius: `${customization.borderRadius}px`,
                        mb: 0.5,
                        alignItems: 'center',
                        backgroundColor: active ? 'rgba(255, 255, 255, 0.08) !important' : 'inherit',
                        py: level > 1 ? 1 : 1.25,
                    }}
                    selected={active}
                    ref={anchorRef}
                    onClick={handleClick}
                >
                    <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}>
                        {menuIcon}
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            leftDrawerOpened ? (
                                <Typography 
                                    variant={active ? 'h5' : 'body1'}  
                                    sx={{ my: 'auto', color: "#fff" }}
                                >
                                    {menu.title}
                                </Typography>
                            ) : (
                                <></>
                            )
                        }
                        secondary={
                            menu.caption && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ ...theme.typography.subMenuCaption }} 
                                    display="block" 
                                    gutterBottom
                                >
                                    {menu.caption}
                                </Typography>
                            )
                        }
                    />
                    {open ? (
                        <IconChevronUp stroke={1.5} fill='#fff' size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
                    ) : (
                        <IconChevronDown stroke={1.5} fill='#fff' size="1rem" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
                    )}
                </ListItemButton>
            </Tooltip>
            {
                leftDrawerOpened ? (
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <List
                            component="div"
                            disablePadding
                            sx={{ml:3}}
                        >
                            {menus}
                        </List>
                    </Collapse>
                ) : (
                    <Popper
                        open={open}
                        anchorEl={anchorRef.current}
                        placement="right-start"
                        disablePortal={false}
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 4],
                                },
                            },
                        ]}
                        style={{ zIndex: 1300 }} 
                    >
                        <ClickAwayListener onClickAway={() => setOpen(false)}>
                            <Paper
                                sx={{
                                    mt: 1,
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: '#12161d',
                                    boxShadow: theme.shadows[3],
                                    minWidth: 200,
                                    zIndex: 1300,
                                }}
                            >
                                <List
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 400,
                                        fontFamily: '"DM Sans", sans-serif !important',
                                        color: '#fff'
                                    }}
                                    onClick={() => setOpen(false)}
                                >
                                    {menus}
                                </List>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                )
            }
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number
};

export default NavCollapse;